import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "src/store";
import {
  QuestionCircleOutlined,
  Tooltip,
  Tour,
  type TourProps,
} from "src/assets/imports/antdComponents";
import {
  getPersonaTutorialStepDefinitions,
  hasSeenPersonaTutorialPage,
  isPersonaTutorialScopeReady,
  markPersonaTutorialPageSeen,
  PERSONA_TOUR_ANCHORS,
  type PersonaTutorialPage,
  type PersonaTutorialScope,
} from "./personaTutorial";

const getTourTarget = (anchorId: string): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  return document.querySelector(`[data-tour-id="${anchorId}"]`) as HTMLElement | null;
};

export const usePersonaTutorialTour = (page: PersonaTutorialPage) => {
  const { t } = useTranslation();
  const { metaDetails, userProfileId } = useSelector((state: RootState) => state.studiesSlice);
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState<TourProps["steps"]>([]);

  const scope = useMemo<PersonaTutorialScope>(
    () => ({
      profileId: userProfileId || metaDetails?.current_profile_id || undefined,
      userId: metaDetails?.id || metaDetails?.user_email || undefined,
    }),
    [metaDetails?.current_profile_id, metaDetails?.id, metaDetails?.user_email, userProfileId],
  );

  const scopeReady = isPersonaTutorialScopeReady(scope);

  const resolveSteps = useCallback(() => {
    const definitions = getPersonaTutorialStepDefinitions(page, t);
    const availableSteps: TourProps["steps"] = definitions
      .filter((step) => Boolean(getTourTarget(step.anchorId)))
      .map((step, index, available) => ({
        title: step.title,
        description: step.description,
        placement: step.placement,
        target: () => getTourTarget(step.anchorId),
        nextButtonProps: {
          children: index === available.length - 1 ? t("Finish") : t("Next"),
        },
        prevButtonProps: {
          children: t("Previous"),
        },
      }));

    setSteps(availableSteps);
    return availableSteps.length > 0;
  }, [page, t]);

  const openTour = useCallback(() => {
    if (resolveSteps()) {
      setOpen(true);
    }
  }, [resolveSteps]);

  const closeTour = useCallback(() => {
    setOpen(false);
    if (scopeReady) {
      markPersonaTutorialPageSeen(page, scope);
    }
  }, [page, scope, scopeReady]);

  useEffect(() => {
    if (!scopeReady || hasSeenPersonaTutorialPage(page, scope)) return;

    const openWhenReady = () => {
      if (!hasSeenPersonaTutorialPage(page, scope) && resolveSteps()) {
        setOpen(true);
      }
    };

    if (typeof window === "undefined") return;
    const frame = window.requestAnimationFrame(openWhenReady);
    return () => window.cancelAnimationFrame(frame);
  }, [page, resolveSteps, scope, scopeReady]);

  const tour = (
    <Tour
      animated
      mask={{ color: "var(--insilico-line-strong)" }}
      onClose={closeTour}
      onFinish={closeTour}
      open={open && Boolean(steps?.length)}
      placement="bottom"
      scrollIntoViewOptions={{ block: "center", behavior: "smooth" }}
      steps={steps}
      type="primary"
    />
  );

  return { openTour, tour };
};

type PersonaTutorialHelpButtonProps = {
  onClick: () => void;
};

export const PersonaTutorialHelpButton: React.FC<PersonaTutorialHelpButtonProps> = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <Tooltip title={t("Open persona tutorial")}>
      <button
        aria-label={t("Open persona tutorial")}
        className="flex size-8 items-center justify-center rounded bg-[var(--insilico-help-bg)] text-[var(--insilico-blue-dark)] transition-colors hover:bg-[var(--insilico-help-hover-bg)]"
        data-tour-id={PERSONA_TOUR_ANCHORS.help}
        onClick={onClick}
        type="button"
      >
        <QuestionCircleOutlined style={{ fontSize: 16 }} />
      </button>
    </Tooltip>
  );
};
